<script setup lang="ts">
import {ref} from "vue";

const faqs = ref([
  {
    q: "I want it faster. Can you rush my order?",
    a: "Yes. Please email us at orders@teambannersports.com with your requested delivery date, and our team will confirm the fastest available production and shipping options."
  },
  {
    q: "What is the size of your pennants?",
    a: "Our pennants are 17\" x 17\"."
  },
  {
    q: "What will the pennants look like when I receive them?",
    a: "Your pennants will arrive individually cut and hole-punched, ready to use. If you prefer a different finish, you can also order pennants in a pole-pocket style."
  },
  {
    q: "Can I request a custom design?",
    a: "Absolutely. We offer custom design support at no extra cost. When you place an order through our order form, you will be able to communicate with our order management team to discuss and refine your design. You can request revisions until you are fully satisfied."
  },
  {
    q: "What is a Pole Pocket banner?",
    a: "A Pole Pocket banner is a 5' x 3' banner, digitally printed on 13-oz vinyl, with the top and bottom edges hemmed inward to create pockets for poles. It is a popular option for parades and display stands."
  },
  {
    q: "Is the logo, photo, or image I upload acceptable for printing?",
    a: "For the best print quality, we recommend images at 300 DPI or higher. You can use the zoom tool in our designer to check image clarity before placing your order."
  },
  {
    q: "Can the banners be hemmed?",
    a: "Yes. Hemmed edges add durability and strength at no additional charge, especially when paired with reinforced grommets."
  },
  {
    q: "What is a Hem & Grommets banner?",
    a: "A Hem & Grommets banner is sewn around the edges and finished with metal grommets, making it easy to hang securely. By default, we place grommets at the four corners, but you can request grommets in any position you prefer at no extra charge."
  },
  {
    q: "Can I change the pocket position on a Pole Pocket banner?",
    a: "Yes. You can request pocket placement based on your display needs. Our default pocket placement is at the top and bottom."
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept Visa, MasterCard, and American Express."
  }
]);
</script>

<template>
  <div c2c-0 class="faq-page">
    <section c2c-1 class="hero">
      <p c2c-2 class="hero-kicker">Help Center</p>
      <h1>Frequently Asked Questions</h1>
      <p c2c-3 class="hero-desc">
        Quick answers about sizes, finishes, custom design requests, and order processing at Team Banner Sports.
      </p>
    </section>

    <section c2c-4 class="faq-section">
      <ul c2c-5 class="faq-list">
        <li v-for="faq in faqs" :key="faq.q" c2c-6 class="faq-item" :class="{active: !!faq.ex}">
          <button type="button" c2c-7 class="faq-question" @click="faq.ex = !faq.ex">
            <span>{{ faq.q }}</span>
            <span c2c-8 class="icon-wrap">
              <span c2c-9 class="down icon-down-open-mini"></span>
              <span c2c-10 class="up icon-up-open-mini"></span>
            </span>
          </button>
          <p c2c-11 class="faq-answer">{{ faq.a }}</p>
        </li>
      </ul>
    </section>

    <p c2c-12 class="contact-note">
      If your question is not listed here, contact us at
      <a href="mailto:orders@teambannersports.com">orders@teambannersports.com</a>
      and we will be happy to help.
    </p>
  </div>
</template>

<style scoped>
.faq-page {
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 22px 14px 44px;
}

.hero {
  border: 1px solid #dbeafe;
  border-radius: 18px;
  padding: 24px 20px;
  background:
    radial-gradient(120% 180% at 100% 0%, rgba(59, 130, 246, 0.14) 0%, rgba(59, 130, 246, 0) 55%),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
}

.hero-kicker {
  margin: 0;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1 {
  margin: 8px 0 0;
  font-size: clamp(30px, 4vw, 42px);
  line-height: 1.08;
  color: #0f172a;
}

.hero-desc {
  margin: 12px 0 0;
  max-width: 760px;
  color: #475569;
  line-height: 1.7;
}

.faq-section {
  margin-top: 22px;
}

.faq-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.faq-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.faq-item + .faq-item {
  margin-top: 10px;
}

.faq-question {
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
}

.faq-question:hover {
  background: #f8fafc;
}

.icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  color: #475569;
  flex-shrink: 0;
}

.up {
  display: none;
}

.faq-item.active .down {
  display: none;
}

.faq-item.active .up {
  display: inline;
}

.faq-answer {
  margin: 0;
  display: none;
  padding: 0 16px 16px;
  color: #334155;
  line-height: 1.75;
}

.faq-item.active .faq-answer {
  display: block;
}

.contact-note {
  margin: 18px 0 0;
  color: #475569;
  font-style: italic;
}

.contact-note a {
  color: #0b5ad9;
  font-weight: 700;
  text-decoration: none;
}

@media (max-width: 560px) {
  .faq-page {
    padding: 16px 10px 36px;
  }

  .hero {
    padding: 18px 14px;
  }

  .faq-question {
    font-size: 16px;
    padding: 12px 12px;
  }

  .faq-answer {
    padding: 0 12px 12px;
  }
}
</style>
<style lang="scss" scoped></style>